import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import Typography from "@material-ui/core/Typography";
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import {emailRegex, rand} from "../../../../utils/function-util";
import * as _ from 'lodash';
import IconButton from '@material-ui/core/IconButton';
import WarningIcon from '@material-ui/icons/Warning';
import Grid from "@material-ui/core/Grid";
import GenericAssignmentError
    from "../../../subscription-assignment/SubscriptionAssignmentPrompts/GenericAssignmentError";
import {addGroupGuest, addProviderGuest} from "../../../../api/pro-guest-functions";
import {StyledInviteUserItem} from "../user/InviteUserItem";
import CSVImport from "../../../_shared/CSVImport";
import {useNotification} from "../../../ui/notification/NotificationProvider";
import {FormStatus, KeyValue, UserRowData} from "../../../../models/shared";
import withEditorComponentStyle from "../../../ui/styles/StyledEditorComponent";

const CSVFormat = {
    headers: ['Email', 'FirstName', 'LastName'],
    examples: [
        ['js.smith@gmail.com', 'John', 'Smith'],
        ['j.doe@gmail.com', 'James', 'Doe']
    ]
};

const buildItemData = (data: any): UserRowData => {
    if (!data || !data.length) {
        return {email: '', firstName: '', lastName: ''};
    }
    return {
        email: data[0],
        firstName: data[1],
        lastName: data[2],
    }
};

const buildItem = (data = []) => ({
    id: rand(),
    data: buildItemData(data),
    errors: {},
    status: FormStatus.none,
    correctiveAction: null,
});

const GuestAssignmentEditor = ({
                                   organizationId,
                                   folderId,
                                   groupId,
                                   onCancel,
                                   onAdded,
                                   onDone,
                                   classes
                               }: any) => {
    const {t, i18n} = useTranslation();
    const [forms, setForms] = React.useState([buildItem()]);
    const [submitted, setSubmitted] = React.useState(false);
    const {pushNotification} = useNotification();

    const failedToAssignSub = (id: any, msg: any) => <GenericAssignmentError message={msg}
                                                                             onCancel={() => handleFormDeleted(id)}/>;
    const processing = forms.some(f => f.status === FormStatus.processing);
    const failedCount = forms.filter(f => f.status === FormStatus.failed).length;
    const allSuccessful = forms.every(f => f.status === FormStatus.successful);

    const addHandler = () => {
        setForms(newForms => ([...newForms, {...buildItem()}]))
    };

    const resetHandler = async (e: any) => {
        setForms([{...buildItem()}]);
        setSubmitted(false);
    };

    const submitForms = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setSubmitted(true);

        const validatedForms = forms.map(f => {
            const errors = ['email', 'firstName', 'lastName']
                .reduce((acc, name) => ({...acc, ...validateField(name, (f.data as any)[name])}), {});

            return {
                ...f,
                errors
            }
        });

        if (validatedForms.some(f => _.values(f.errors).some((v: any) => !!v)))
            return setForms(validatedForms);

        const formsToProcess = forms.filter(f => f.status !== FormStatus.successful && f.status !== FormStatus.processing);

        for (let i = 0; i < formsToProcess.length; i++) {
            await submitForm(formsToProcess[i].id);
        }

        if (onAdded) onAdded();
    };

    const submitForm = async (id: any) => {
        const userForm = forms.find(f => f.id === id);

        if (!userForm) return;

        updateForm(id, {status: FormStatus.processing});
        try {
            const guest = {
                ...userForm.data,
                language: i18n.language
            };

            if (groupId) {
                await addGroupGuest({organizationId, folderId, groupId, guest})
            } else {
                await addProviderGuest(organizationId, guest);
            }

            updateForm(id, {status: FormStatus.successful, correctiveAction: null});
        } catch (err) {
            console.log(err);
            const errorType = _.get(err, 'response.data.error.type') || 'UnknownError';
            let correctiveAction = null;
            const msg = `_Common.Api.Messages.${errorType}`;
            correctiveAction = failedToAssignSub(id, msg);
            updateForm(id, {status: FormStatus.failed, correctiveAction});
        }
    };

    const updateForm = (id: any, patch: any) => {
        setForms(newForms => {
            const index = newForms.findIndex(f => f.id === id);
            if (index <= -1) return newForms;
            const result = [...newForms];
            result[index] = _.merge({}, newForms[index], patch);
            return result;
        });
    };

    const handleFormChange = (name: any, value: any, form: any) => {
        const errors = validateField(name, value);
        const update = {
            data: {[name]: value},
            errors: errors
        };
        updateForm(form.id, update);
    };

    const handleFormDeleted = (id: any) => {
        setForms(formsToDelete => {
            const index = formsToDelete.findIndex(f => f.id === id);
            if (index <= -1) return formsToDelete;
            const newForms = [...formsToDelete];
            newForms.splice(index, 1);
            return newForms;
        });
    };

    const validateField = (field: any, value: any) => {
        const errors: KeyValue<string> = {};
        if (field === 'email') {
            errors[field] = emailRegex.test(value) ? null : 'EmailIsInvalid';
        }
        else {
            errors[field] = !value ? 'FieldIsRequired' : null;
        }
        return errors;
    };

    const loadCSVData = (data: any) => {
        setForms(data.map((row: any) => buildItem(row)));
    };

    const onLoadCSVError = (errorMsg: any, errorCode: any) => {
        const errorCodeMsg = errorCode ? '\nError code: ' + errorCode : '';
        if (errorMsg) {
            pushNotification({
                message: errorMsg + errorCodeMsg,
                severity: 'warning'
            });
        }
    };

    return <Fragment>
        {
            failedCount > 0 &&
            <Grid container className={classes.errorBanner} justify='center'>
                <WarningIcon/>
                <Typography variant="body2">
                    {t('Subscription.SubscriptionAssignment.XEntriesNeedAttention', {failedCount})}
                </Typography>
            </Grid>
        }
        {!!groupId && <Grid item xs={12}>
            <Typography variant="subtitle1">{t('ProGuest.Groups.Tabs.Guests.Description')}</Typography>
        </Grid>
        }
        <Grid container className={classes.formsContainer}>
            {
                forms.map((f, i) => <StyledInviteUserItem
                        form={f}
                        key={f.id.toString()}
                        handleFormDeleted={handleFormDeleted}
                        handleFormChange={handleFormChange}
                        submitted={submitted}>
                        {f.correctiveAction}
                    </StyledInviteUserItem>
                )
            }
        </Grid>
        <Grid container justify="flex-start">
            <IconButton aria-label="add" className={classes.addBtn} onClick={addHandler}>
                <AddIcon/>
            </IconButton>
        </Grid>
        <Grid container justify="flex-end" className={classes.actions}>
            {
                allSuccessful ?
                    <Button variant="contained" color="primary" size="medium"
                            onClick={onDone}
                            disabled={processing}>
                        {t('_Common.Labels.Done')}
                    </Button> :
                    <Fragment>
                        <Button variant="outlined" color="primary" size="medium"
                                onClick={onCancel}
                                disabled={processing}>
                            {t('_Common.Labels.Cancel')}
                        </Button>
                        <Button variant="outlined" color="primary" size="medium"
                                onClick={resetHandler}
                                disabled={processing}>
                            {t('_Common.Labels.Reset')}
                        </Button>
                        <Button variant="contained" color="primary" size="medium"
                                onClick={submitForms}
                                disabled={processing}>
                            {t('_Common.Labels.Add')}
                        </Button>
                    </Fragment>
            }
        </Grid>
        <CSVImport displayedLabel={t('_Common.Labels.CSV')}
                   format={CSVFormat}
                   onComplete={loadCSVData}
                   onError={onLoadCSVError}/>
    </Fragment>
};

export const StyleGuestAssignmentEditor =  withEditorComponentStyle(GuestAssignmentEditor);
